import { DesignLayoutType, EventPageFragment, EventPageType } from '@graphql/generated';
import { GuestSiteEvent } from './layout.types';
import { darkenColor, isLightColor } from '@shared/utils/color';
import { toRgbArray } from '@shared/utils/color';
import { ButtonV2Props, defaultTheme } from '@withjoy/joykit';
import chroma from 'chroma-js';
import { fadeColor } from '@apps/admin/routes/DashboardPreview/DashboardPreview.utils';
import { SupportedHeaderPhotoLayoutSchema, WebsitePhotoCropInfo } from '@apps/guest/websiteHeader';
import { WebsitePhotoBaseProperties } from '@apps/guest/websiteHeader';

const nonSupportedEventPageSet = new Set([EventPageType.guests, EventPageType.tidbits]);
export const isSupportedEventPage = (pageType: EventPageType) => {
  return !nonSupportedEventPageSet.has(pageType);
};

export const findEventPageByPageSlug = (pages: ReadonlyArray<EventPageFragment>, target: string, isCustomPage?: boolean): Readonly<EventPageFragment> | undefined => {
  return pages.find(
    page => isSupportedEventPage(page.type) && page.pageSlug.toLocaleLowerCase() === target.toLocaleLowerCase() && (!isCustomPage || page.type === EventPageType.custom),
  );
};

export const findEventPageByType = (pages: ReadonlyArray<EventPageFragment>, target: EventPageType): Readonly<EventPageFragment> | undefined => {
  return pages.find(page => isSupportedEventPage(page.type) && page.type === target);
};

export const shouldRenderGraphicAccent = (accent: Maybe<string>) => !!accent && accent !== 'blank';

export const getEventMenuPropsFromEvent = (event: GuestSiteEvent) => {
  return {
    eventId: event?.id || '',
    layoutType: event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha,
    dateInMilliseconds: event.info?.dateV0918?.milliseconds || 0,
    pages: event?.pages || [],
    brannanNavigation: event?.eventDesign?.brannanNavigation,
  };
};

export const getButtonPropsBasedOnBackground = (backgroundColor: string) => {
  const buttonProps: Partial<ButtonV2Props> = {};
  const { colors } = defaultTheme;

  if (backgroundColor) {
    buttonProps.size = 'lg';
    buttonProps.border = `1px soild ${colors.gray5}`;
    buttonProps.shape = 'rounded';

    if (isLightColor(...toRgbArray(backgroundColor))) {
      buttonProps.variant = 'solid';
      buttonProps.backgroundColor = colors.black;
      buttonProps._hover = {
        backgroundColor: fadeColor(colors.black, 0.8),
        color: fadeColor(colors.white, 0.8),
      };
      buttonProps._active = {
        backgroundColor: fadeColor(colors.black, 0.9),
        color: fadeColor(colors.white, 0.9),
      };
    } else {
      buttonProps.variant = 'outline';
      buttonProps.backgroundColor = colors.white;
      buttonProps.color = colors.black;
      buttonProps._hover = {
        backgroundColor: fadeColor(colors.white, 0.8),
        color: fadeColor(colors.black, 0.8),
      };
      buttonProps._active = {
        backgroundColor: fadeColor(colors.white, 0.9),
        color: fadeColor(colors.black, 0.9),
      };
    }
  }

  return buttonProps;
};

export const getHoverColor = (color: string): string => {
  const darkenedColor = darkenColor(color);
  return chroma(darkenedColor).brighten(0.2).hex();
};

export const getActiveColor = (color: string): string => {
  const darkenedColor = darkenColor(color);
  return chroma(darkenedColor).darken(0.2).hex();
};

export const getSolidButtonStylesWithAccentAndTextColor = (accentColor: string, textColor: string): ButtonV2Props => {
  const darkenedColor = darkenColor(accentColor);
  const backgroundColorHover = chroma(accentColor).brighten(0.2).hex();
  const backgroundColorActive = chroma(accentColor).darken(0.2).hex();

  return {
    backgroundColor: darkenedColor,
    _hover: { color: textColor, backgroundColor: backgroundColorHover },
    _active: { color: textColor, backgroundColor: backgroundColorActive },
  };
};

export const getButtonOutlineStylesWithAccentColorAndTextColor = (accentColor: string, textColor: string): ButtonV2Props => {
  const darkenedColor = darkenColor(accentColor);
  const hoverColor = getHoverColor(accentColor);
  const activeColor = getActiveColor(accentColor);
  const backgroundColorHover = chroma(accentColor).alpha(0.1).hex();
  const backgroundColorActive = chroma(accentColor).alpha(0.2).hex();
  const disabledColor = chroma(accentColor).alpha(0.5).hex();

  return {
    borderColor: darkenedColor,
    color: darkenedColor,
    _hover: { borderColor: hoverColor, color: hoverColor, backgroundColor: backgroundColorHover },
    _active: { borderColor: activeColor, color: activeColor, backgroundColor: backgroundColorActive },
    _disabled: { borderColor: disabledColor, color: disabledColor, backgroundColor: backgroundColorHover },
  };
};

export const getButtonStylesWithAccentColor = (accentColor: string): ButtonV2Props => {
  const hoverColor = getHoverColor(accentColor);
  const activeColor = getActiveColor(accentColor);
  const backgroundColorHover = chroma(accentColor).alpha(0.1).hex();
  const backgroundColorActive = chroma(accentColor).alpha(0.2).hex();
  const disabledColor = chroma(accentColor).alpha(0.5).hex();

  return {
    borderColor: accentColor,
    color: accentColor,
    _hover: { borderColor: hoverColor, color: hoverColor, backgroundColor: backgroundColorHover },
    _active: { borderColor: activeColor, color: activeColor, backgroundColor: backgroundColorActive },
    _disabled: { borderColor: disabledColor, color: disabledColor, backgroundColor: backgroundColorHover },
  };
};

export const getImageRendition = (url: string) => {
  const imageUrl = new URL(url);
  imageUrl.searchParams.append('rendition', 'medium');
  return imageUrl.toString();
};

export const getHeaderPhotoAspectRatio = (cropInfo?: WebsitePhotoCropInfo, photo?: WebsitePhotoBaseProperties) => {
  if (cropInfo && photo) {
    let cropWidth = cropInfo.width;
    let cropHeight = cropInfo.height;
    if (cropInfo?.unit === 'percent') {
      cropWidth = (cropInfo.width / 100) * photo.width;
      cropHeight = (cropInfo.height / 100) * photo.height;
    }
    const aspectRaio = cropWidth / cropHeight;
    return aspectRaio;
  }
  // retuns default 16/9 if photoPresentation cropInfo is not available
  return 16 / 9;
};

type HeaderLayoutWithPhotos<T> = T extends { photos?: unknown } ? T & { photos: NonNullable<T['photos']> } : never;

export const isHeaderLayoutWithPhotos = <T extends SupportedHeaderPhotoLayoutSchema>(layout: T): layout is HeaderLayoutWithPhotos<T> => {
  return Boolean('photos' in layout && layout.photos);
};

type HeaderLayoutWithJsonContainerId<T> = T extends { jsonContainerId?: unknown } ? T & { jsonContainerId: T['jsonContainerId'] } : never;

export const isHeaderLayoutWithJsonContainerId = <T extends SupportedHeaderPhotoLayoutSchema>(layout: T): layout is HeaderLayoutWithJsonContainerId<T> => {
  return Boolean('jsonContainerId' in layout);
};
